import moment from 'moment';
import { ReactNode, PureComponent } from 'react';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Translate } from '../../../../../api/pushVisit';
import { PAD_CONFIG_FOLLOW_UP_ADVICES_ID } from '../../../../../constants';
import { Actions } from '../../../../../store/actions';
import { clearPP, hideDialog, showDialog } from '../../../../../store/actions/prescriptionPad';
import { GlobalState } from '../../../../../store/reducers';
import {
    getDrugsSubstitute,
    getFollowUp,
    getIsSubstitutionVisible,
    getMedications,
    getTranslate,
    getPrescription,
} from '../../../../../store/selectors/prescriptionPad';
import { Followup, MedicationsEntity, Prescription } from '../../../../../store/types/prescription';
import { track } from '../../../../../tracking';
import { globeIcon, previewIcon, settingsIcon } from '../../../../common/Icons';
import WithBodyClickClose from '../../../../common/WithBodyClickClose';
import {
    LangDialogComponent,
    LANG_CODE_TO_NAME,
    PrintConfigDialog,
} from '../../previewScreen/PreviewPanel';
import FinishBt from './FinishBt';
import { PushToCloudBt } from './PushToCloudBt';
import { focusCard } from '../focusCard';
import { SubstitutesResponse } from '../../../../../api/fetchDrugsSubstitute';
import { ReplaceHEMedicinesModal } from './ReplaceHEMedicinesModal';
import { DocumentsEntity } from '../../../../../api/fetchMedicalRecords';
import { his } from '../../../../../pages/history';
import { GO_TO_PATIENTS_DETAILS } from '../../../../../routes/paths';
import { getIsNotesMode } from '../../../../../store/selectors/session';
import AskAssist from './AskAssist';
import { amIDoctor, amIDoctorOnly } from '../../../../../store/idbHandler';

interface Props {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    translate: Translate;
    followUp?: Followup;
    drugsSubstitute?: SubstitutesResponse;
    medications?: MedicationsEntity[];
    newMrs?: DocumentsEntity[] | null | undefined;
    patientId?: string;
    isSubstitutionVisible?: boolean;
    isNotesMode?: boolean;
    rx?: Prescription;
}

type State = {
    isPrintSettingDialogVisible?: boolean;
    langDialogVisible: boolean;
    showNewMrStrip?: boolean;
};

class BottomActionStrip extends PureComponent<Props> {
    state: State = {
        isPrintSettingDialogVisible: false,
        langDialogVisible: false,
        showNewMrStrip: true,
    };
    settingsIcon = settingsIcon('fill-white');
    globeIcon = globeIcon('fill-white');

    onClear = (): void => {
        this.props.dispatch(clearPP());
    };

    showPrintSettingDialog = (): void => {
        this.setState({
            isPrintSettingDialogVisible: true,
        });
    };

    hidePrintSettingDialog = (): void => {
        this.setState({
            isPrintSettingDialogVisible: false,
        });
    };

    onViewSummary = (): void => {
        this.props.dispatch(showDialog('summary'));
        return;
    };

    hideLangDialog = (): void => {
        track('TOGGLE_TRANSLATE_DIALOG', { visible: 'false' });
        this.setState({
            langDialogVisible: false,
        });
    };

    toggleLangDialog = (): void => {
        track('TOGGLE_TRANSLATE_DIALOG', {
            visible: (!this.state.langDialogVisible).toString(),
        });
        this.setState({
            langDialogVisible: !this.state.langDialogVisible,
        });
    };

    onFollowupButtonClick = () => {
        focusCard(PAD_CONFIG_FOLLOW_UP_ADVICES_ID);
    };

    toggleReplaceMedicinesModal = (): void => {
        const { dispatch, isSubstitutionVisible } = this.props;
        if (isSubstitutionVisible) dispatch(hideDialog('substitutions'));
        else dispatch(showDialog('substitutions'));
    };

    gotoMedicalRecords = () => {
        his.replace(GO_TO_PATIENTS_DETAILS(this.props.patientId ?? '', 'medical-records'));
    };

    action = new URLSearchParams(window.location.search).get('action');

    render(): ReactNode {
        const { isPrintSettingDialogVisible, langDialogVisible } = this.state;
        const {
            translate,
            followUp,
            drugsSubstitute,
            medications,
            isSubstitutionVisible,
            isNotesMode,
            rx,
        } = this.props;
        const day = followUp?.date && moment(followUp.date).format('dddd');
        const isFollowupOnWeekend = day === 'Sunday' || day === 'Saturday';

        return (
            <div className="flex items-center justify-between absolute rounded-8 bg-actionPrimary bottom-24 inset-x-10 px-16 py-8 cta-row z-1">
                <div className="flex items-center space-x-16">
                    <button
                        onClick={this.onClear}
                        className="text-btn-v2 px-16 py-8 btn--small padv2-main-panel"
                    >
                        Clear
                    </button>
                    {!this.action && isPrintSettingDialogVisible && (
                        <WithBodyClickClose closeMe={this.hidePrintSettingDialog}>
                            <PrintConfigDialog close={this.hidePrintSettingDialog} />
                        </WithBodyClickClose>
                    )}
                    {this.action || isNotesMode ? null : (
                        <button
                            onClick={
                                isPrintSettingDialogVisible
                                    ? this.hidePrintSettingDialog
                                    : this.showPrintSettingDialog
                            }
                            className="px-16 py-10 lg:py-8 btn--small secondary-btn-v2"
                        >
                            <div className="flex items-center justify-center w-16 h-16">
                                {this.settingsIcon}
                            </div>
                            <p className="hidden lg:flex select-none">Print Settings</p>
                        </button>
                    )}
                    {!this.action && !isNotesMode && langDialogVisible && (
                        <WithBodyClickClose closeMe={this.hideLangDialog}>
                            <LangDialogComponent close={this.hideLangDialog} />
                        </WithBodyClickClose>
                    )}

                    {!this.action && (
                        <button
                            onClick={this.toggleLangDialog}
                            className="relative px-16 py-10 lg:py-8 ml-16 btn--small secondary-btn-v2"
                        >
                            <div className="flex items-center justify-center w-16 h-16">
                                {this.globeIcon}
                            </div>
                            <p className="hidden lg:flex lg:min-w-108 select-none">
                                {translate.flag
                                    ? LANG_CODE_TO_NAME[translate.lang]
                                    : 'Select Language'}
                            </p>
                        </button>
                    )}
                    {amIDoctor() && rx && <AskAssist docid={rx?.doctor._id} />}
                </div>

                {isNotesMode ? null : <PushToCloudBt />}
                <div className="flex items-center space-x-16">
                    {isNotesMode ? null : (
                        <button
                            onClick={this.onViewSummary}
                            className="px-16 py-8 btn--small secondary-btn-v2"
                        >
                            <div className="flex items-center justify-center w-16 h-16">
                                {previewIcon('w-16 h-16 fill-white')}
                            </div>
                            <p>Preview</p>
                        </button>
                    )}
                    <FinishBt />
                </div>

                {isFollowupOnWeekend && (
                    <div
                        className="flex items-center justify-center space-x-8 w-241 py-10 absolute right-2 bottom-56 border rounded-8 border border-darwin-status-warning-500 bg-darwin-status-warning-100 cursor-pointer"
                        onClick={this.onFollowupButtonClick}
                    >
                        <div>
                            <svg
                                className="w-18 h-18 fill-darwin-status-warning-500"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 640 512"
                            >
                                <path d="M96 256a224 224 0 1 1 448 0A224 224 0 1 1 96 256zM344 152V128H296v24V264v24h48V264 152zM296 320v48h48V320H296zM91.1 109C63.8 151.4 48 201.8 48 256s15.8 104.6 43.1 147l13 20.2-40.4 26L50.7 429C18.6 379.1 0 319.7 0 256S18.6 132.9 50.7 83l13-20.2 40.4 26L91.1 109zM589.3 83C621.4 132.9 640 192.3 640 256s-18.6 123.1-50.7 173l-13 20.2-40.4-26 13-20.2c27.3-42.4 43.1-92.8 43.1-147s-15.8-104.6-43.1-147l-13-20.2 40.4-26 13 20.2z" />
                            </svg>
                        </div>

                        <div className="font-700 text-darwin-status-warning-500">
                            Followup on {day}
                        </div>
                    </div>
                )}

                {medications &&
                    medications.length > 0 &&
                    drugsSubstitute &&
                    drugsSubstitute.length > 0 && (
                        <div className="flex items-start justify-center space-x-8 py-10 px-12 absolute right-2 bottom-56 border rounded-8 border border-darwin-status-warning-500 bg-darwin-status-warning-100 select-none">
                            <div>
                                <svg
                                    className="w-24 h-24 fill-darwin-status-warning-500"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M5.75 9.1875V12.25H8.375V9.1875C8.375 8.47656 7.77344 7.875 7.0625 7.875C6.32422 7.875 5.75 8.47656 5.75 9.1875ZM4 9.1875C4 7.51953 5.36719 6.125 7.0625 6.125C8.73047 6.125 10.125 7.51953 10.125 9.1875V9.87109C10.207 8.96875 10.6445 8.14844 11.4102 7.60156C12.7773 6.58984 14.6914 6.94531 15.6758 8.36719L19.1758 13.4805C20.1602 14.9023 19.832 16.8438 18.4375 17.8281C17.043 18.8125 15.1289 18.4844 14.1719 17.0625L10.6445 11.9492C10.3438 11.4844 10.1523 10.9648 10.125 10.4453V15.3125C10.125 17.0078 8.73047 18.375 7.0625 18.375C5.36719 18.375 4 17.0078 4 15.3125V9.1875ZM12.0938 10.9648L13.8711 13.5352L16.0586 12.0039L14.2266 9.35156C13.8164 8.72266 12.9961 8.61328 12.4219 9.02344C11.8203 9.43359 11.6562 10.3086 12.0938 10.9648ZM17.4258 16.4062L17.4531 16.3789L17.3438 16.4609C17.3711 16.4336 17.3984 16.4336 17.4258 16.4062Z" />
                                </svg>
                            </div>

                            <div className="space-y-4">
                                <p className="font-700 text-darwin-status-warning-500">
                                    {drugsSubstitute.length} Medicine needs to be substituted due to
                                    unavailability.
                                </p>
                                <div className="flex items-center justify-between">
                                    <p
                                        className="font-700 text-13 text-darwin-primary-500 cursor-pointer"
                                        onClick={this.toggleReplaceMedicinesModal}
                                    >
                                        View substitutes
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                {drugsSubstitute && (
                    <ReplaceHEMedicinesModal
                        isVisible={isSubstitutionVisible}
                        closeMe={this.toggleReplaceMedicinesModal}
                        drugsSubstitute={drugsSubstitute}
                    />
                )}
            </div>
        );
    }
}

export default connect((state: GlobalState) => ({
    translate: getTranslate(state),
    followUp: getFollowUp(state),
    drugsSubstitute: getDrugsSubstitute(state),
    medications: getMedications(state),
    isSubstitutionVisible: getIsSubstitutionVisible(state),
    isNotesMode: getIsNotesMode(state),
    rx: getPrescription(state),
}))(BottomActionStrip);
